"use client";

import { useState } from "react";
import { ArrowUpRight, Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const manifestoItems = [{
  question: "What are your material standards for industrial labels?",
  answer: "We utilize industrial-grade phenolic, stainless steel, and UV-stabilized polymers. Every material is selected for its resistance to extreme heat, chemical exposure, and mechanical wear, ensuring your identification systems remain legible for decades.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_material_standards.webp",
  imageAlt: "Close up of industrial grade phenolic material"
}, {
  question: "How do you ensure compliance with UL 508A standards?",
  answer: "Our engraving and marking processes are strictly aligned with UL 508A requirements. We maintain rigorous documentation and traceability for every component, ensuring your panel builds meet all safety and regulatory certifications required by inspectors.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/ul_compliance_standards.png",
  imageAlt: "Technician inspecting UL compliant labels"
}, {
  question: "What is your typical turnaround for submittal packages?",
  answer: "We prioritize efficiency in our workflow. Once your specifications are finalized, we generate comprehensive submittal packages within 48 hours, allowing you to secure project approvals and move directly into the production phase without delay.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/submittal_package_process.webp",
  imageAlt: "Digital submittal package on a tablet screen"
}, {
  question: "Can you handle high-volume direct part marking?",
  answer: "Yes, our facility is equipped with advanced fiber laser systems capable of high-speed Direct Part Marking (DPM). We provide permanent, high-contrast serialization and branding on metal components, optimized for both manual and automated scanning.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/dpm_marking_facility.png",
  imageAlt: "Laser marking machine in operation"
}, {
  question: "How do you manage custom engraving requests?",
  answer: "We specialize in bespoke solutions. Whether you need unique mounting configurations, specific color-coded phenolic, or complex graphic integration, our design team works directly with your CAD files to ensure precision execution.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/custom_engraving_work.png",
  imageAlt: "Custom engraved metal plate with intricate details"
}, {
  question: "What is your protocol for arc flash label accuracy?",
  answer: "Accuracy is our primary directive. We calculate incident energy levels based on your provided power system studies, ensuring every arc flash label is compliant with NFPA 70E standards and clearly communicates the necessary safety boundaries.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/arc_flash_label_protocol.png",
  imageAlt: "Safety technician applying arc flash label"
}, {
  question: "Do you offer shipping and logistics support?",
  answer: "We provide secure, organized shipping for all orders. Labels are grouped by project or panel, clearly labeled for easy installation on-site, and shipped via reliable carriers to ensure your components arrive on schedule.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/shipping_logistics_support.png",
  imageAlt: "Organized shipping crates in warehouse"
}, {
  question: "How do I request a quote for my project?",
  answer: "Simply upload your project specifications or drawings through our contact portal. Our team will review your requirements and provide a detailed, competitive quote, typically within one business day.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/quote_request_process.png",
  imageAlt: "Professional reviewing project blueprints"
}];
export default function () {
  const [openValue, setOpenValue] = useState<string>("item-0");
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1664" id="faq-accordion" className="relative bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-16 lg:px-22">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5 relative">
            <div ref={headerRef} className={`lg:sticky lg:top-28 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              {}
              <div aria-hidden="true" className="pointer-events-none absolute -top-10 -left-4 lg:-left-8 select-none font-serif font-light leading-none text-foreground/5" style={{
              fontSize: "20vw"
            }}>
                05
              </div>

              {}
              <div className="relative flex items-center gap-3 mb-8">
                <span className="font-mono text-xs uppercase tracking-[0.18em] text-primary">
                  § 05 / OPERATIONS — STANDARDS &amp; QUALITY
                </span>
              </div>

              {}
              <div className="relative flex items-center mb-10">
                <div className="h-px w-20 bg-primary" />
                <div className="h-1 w-1 bg-primary" />
              </div>

              {}
              <h2 className="relative font-default font-light leading-[1.0] tracking-tight text-foreground mb-8" style={{
              fontSize: "clamp(2.25rem, 5.4vw, 4.75rem)"
            }}>
                The Industrial{" "}
                <span className="italic text-primary">Standard</span>
              </h2>

              {}
              <p className="relative font-default text-foreground/85 leading-relaxed mb-10 max-w-md" style={{
              fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)"
            }}>
                Eight protocols. One commitment. Precision in every mark.
              </p>

              {}
              <Link to="/ContactPage" className="relative inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-primary group">
                <span className="relative">
                  Request Quote
                  <span className="absolute left-0 -bottom-1 h-px bg-primary transition-all duration-300 w-[60%] group-hover:w-full" />
                </span>
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible value={openValue} onValueChange={v => setOpenValue(v)} className="w-full">
              {manifestoItems.map((item, i) => {
              const itemValue = `item-${i}`;
              const isOpen = openValue === itemValue;
              const indexLabel = `${String(i + 1).padStart(2, "0")} / ${String(manifestoItems.length).padStart(2, "0")}`;
              return <AccordionItem key={i} data-index={i} value={itemValue} className="relative border-b-0 border-t border-foreground/20">
                    {}
                    <span aria-hidden="true" className={`pointer-events-none absolute left-0 top-0 w-[2px] bg-primary transition-all duration-300 ease-out ${isOpen ? "h-full opacity-100" : "h-0 opacity-0"}`} />

                    <AccordionTrigger className={`group flex items-center justify-between gap-6 py-6 hover:no-underline rounded-md transition-colors duration-300 [&>svg]:hidden ${isOpen ? "text-foreground" : "text-foreground/85"}`}>
                      <span className="flex-1 text-left font-default font-normal leading-tight tracking-tight" style={{
                    fontSize: "clamp(1.125rem, 1.8vw, 1.5rem)"
                  }}>
                        {item.question}
                      </span>
                      <span className="hidden sm:block font-mono text-xs uppercase tracking-[0.18em] text-foreground/55 shrink-0">
                        {indexLabel}
                      </span>
                      <span className={`shrink-0 inline-flex items-center justify-center w-7 h-7 text-primary transition-all duration-300 ease-out ${isOpen ? "rotate-45 opacity-100" : "rotate-0 opacity-70 group-hover:opacity-100"}`} aria-hidden="true">
                        <Plus className="h-5 w-5" strokeWidth={1.25} />
                      </span>
                    </AccordionTrigger>

                    <AccordionContent className="pb-8 pt-2 pl-0 md:pl-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                        <p className="md:col-span-2 font-default text-foreground/85 leading-relaxed" style={{
                      fontSize: "0.9375rem",
                      lineHeight: 1.7
                    }}>
                          {item.answer}
                        </p>

                        {}
                        <div className="hidden md:block relative">
                          <div className="relative border border-foreground/15 aspect-[4/5] overflow-hidden">
                            {}
                            <span aria-hidden="true" className="absolute top-2 left-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-primary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-primary" />
                            </span>
                            <span aria-hidden="true" className="absolute top-2 right-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-primary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-primary" />
                            </span>
                            <span aria-hidden="true" className="absolute bottom-2 left-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-primary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-primary" />
                            </span>
                            <span aria-hidden="true" className="absolute bottom-2 right-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-primary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-primary" />
                            </span>

                            <img src={item.image} data-wvc-srcset={`${item.image}-wvc-srcset`} data-wvc-sizes={`${item.image}-wvc-sizes`} alt={item.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out hover:scale-[1.03]" />
                          </div>
                          <div className="mt-3 font-mono text-[0.7rem] uppercase tracking-[0.18em] text-foreground/55">
                            FIG. {String(i + 1).padStart(2, "0")} — DETAIL
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>;
            })}

              {}
              <div className="relative border-t border-foreground/20 pt-6 flex items-center gap-2">
                <div className="h-1 w-1 bg-primary" />
                <div className="h-px w-16 bg-primary" />
                <span className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-foreground/55 ml-2">
                  End of operations
                </span>
              </div>
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}